import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface BasketItem {
	id: string;
	name: string;
	url: string;
	price?: string;
}

export interface BasketListItem extends BasketItem {
	readonly basketItemId: string;
}
interface ReplaceItemQuantityPayload {
	item: BasketItem;
	quantity: number;
}

interface BasketEntry extends BasketItem {
	readonly entryId: string;
	readonly prefix: number;
}
export type BasketState = BasketEntry[];

const initialState: BasketState = [];

export const selectBasketItems = (state: { basket: BasketState }): BasketListItem[] =>
	state.basket.map(({ entryId, prefix, ...item }) => ({
		...item,
		basketItemId: entryId,
	}));

const basketSlice = createSlice({
	name: 'basket',
	initialState,
	reducers: {
		addItem: (state, action: PayloadAction<BasketItem>) => {
			const itemsWithSameId = state.filter((item) => item.id === action.payload.id);
			const maxPrefix = itemsWithSameId.length > 0 ? Math.max(...itemsWithSameId.map((item) => item.prefix)) : 0;
			const nextPrefix = maxPrefix + 1;

			state.push({
				...action.payload,
				entryId: `${action.payload.id}#${nextPrefix}`,
				prefix: nextPrefix,
			});
		},
		removeItem: (state, action: PayloadAction<string>) => {
			return state.filter((item) => item.entryId !== action.payload);
		},
		removeItemsByProductId: (state, action: PayloadAction<string>) => {
			return state.filter((item) => item.id !== action.payload);
		},
		updateItemQuantity: (state, action: PayloadAction<ReplaceItemQuantityPayload>) => {
			const { item, quantity } = action.payload;
			const existingItems = state.filter((basketItem) => basketItem.id === item.id);
			if (quantity <= existingItems.length) {
				const removedIds = new Set(existingItems.slice(quantity).map((entry) => entry.entryId));
				return state.filter((entry) => !removedIds.has(entry.entryId));
			}

			const maxPrefix = Math.max(0, ...existingItems.map((entry) => entry.prefix));
			const newItems: BasketEntry[] = Array.from({ length: quantity - existingItems.length }, (_, index) => {
				const prefix = maxPrefix + index + 1;
				return {
					...item,
					entryId: `${item.id}#${prefix}`,
					prefix,
					name: prefix > 1 ? `${item.name} ${prefix}` : item.name,
				};
			});
			const lastItem = existingItems.at(-1);
			const insertAt = lastItem
				? state.findIndex((entry) => entry.entryId === lastItem.entryId) + 1
				: state.length;
			state.splice(insertAt, 0, ...newItems);
		},
	},
});

export const { addItem, removeItem, removeItemsByProductId, updateItemQuantity } = basketSlice.actions;
export default basketSlice.reducer;
